import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";

// Load all generated JSON Schemas at build time
// Note: Vite will inline JSON modules; we keep types as any for flexibility here
// The schemas are generated in `src/schemas/` by the lightweight generator.
// Pattern covers files like `policy.Policy.schema.json`, `soa.SOAData.schema.json`, etc.
const schemaModules = import.meta.glob("/src/schemas/*.json", { eager: true }) as Record<string, any>;
const getSchemaFromModule = (mod: any) => (mod && typeof mod === 'object' && 'default' in mod ? mod.default : mod);
const SCHEMAS = Object.entries(schemaModules)
  .map(([key, mod]) => {
    const schema = getSchemaFromModule(mod) as any;
    const title = (schema && typeof schema.title === 'string') ? schema.title : (schema?.$id || key);
    return { key, title, schema };
  })
  .sort((a, b) => a.title.localeCompare(b.title));

interface FnRunnerProps {
  fn: (...args: any[]) => any;
  fnName: string;
  onClose: () => void;
}

// Map of known parameter types for special handling
const KNOWN_PARAM_TYPES: Record<string, any> = {
  assetData: {
    // This is a simplified version; in practice, this should be auto-generated from the TS interface
    assetCat: { type: 'string' },
    assetCriticality: { type: 'string', options: ['Low', 'Medium', 'High'] },
    assetDesc: { type: 'string' },
    assetID: { type: 'string' },
    assetIDstatus: { type: 'string', options: ['active', 'inactive', 'deleted'] },
    assetLocation: { type: 'string' },
    assetOwner: { type: 'string' },
    assetPublishInPrivacyPolicy: { type: 'string', options: ['Yes', 'No'] },
    assetRisk: { type: 'string' },
    assetSubprocessor: { type: 'string', options: ['Yes', 'No'] },
    assetTeam: { type: 'string' },
    backupLocation: { type: 'string' },
    backupNeeded: { type: 'string', options: ['Yes', 'No'] },
    backupRetention: { type: 'string' },
    classification: { type: 'string' },
    customFields: { type: 'array' },
    dataStorageLocations: { type: 'array' },
    dataTypesShared: { type: 'array' },
    dataTypesSharedString: { type: 'string' },
    dateAdded: { type: 'string' },
    label: { type: 'string' },
    lastReviewDate: { type: 'string' },
    legalBits: { type: 'string' },
    nextReviewDate: { type: 'string' },
    portability: { type: 'string', options: ['Yes', 'No'] },
    securityReview: { type: 'string', options: ['Yes', 'No'] },
    serial: { type: 'string' },
    socCriticality: { type: 'string' },
    socSubserviceOrganisation: { type: 'string' },
    status: { type: 'string', options: ['Active', 'Decommissioned', 'Deleted'] },
    vendorManagementNeeded: { type: 'string', options: ['Yes', 'No'] },
    vendorWebsite: { type: 'string' },
  }
};

function guessParamMeta(fn: Function): { name: string; type: string; options?: any[]; fields?: any }[] {
  const fnStr = fn.toString();
  const match = fnStr.match(/^[^(]*\(([^)]*)\)/);
  const params = match ? match[1].split(',').map(p => p.trim()).filter(Boolean) : [];
  // Guess type by name for known cases
  return params.map(name => {
    if (KNOWN_PARAM_TYPES[name]) {
      return { name, type: 'object', fields: KNOWN_PARAM_TYPES[name] };
    }
    return { name, type: 'string' };
  });
}

export function FnRunner({ fn, fnName, onClose }: FnRunnerProps) {
  const params = guessParamMeta(fn);

  const getSchemaByKey = (key: string | null | undefined) => {
    if (!key) return undefined;
    return SCHEMAS.find(s => s.key === key)?.schema as any | undefined;
  };

  function deref(ref: string, root: any): any | undefined {
    if (!ref || typeof ref !== 'string') return undefined;
    if (ref.startsWith('#/$defs/')) {
      const name = ref.replace('#/$defs/', '');
      return root?.$defs?.[name];
    }
    return undefined;
  }

  function normalizeSchema(s: any, root: any): any {
    if (s && typeof s === 'object' && s.$ref) {
      const target = deref(s.$ref, root);
      if (target) return normalizeSchema(target, root);
    }
    return s;
  }

  function collectPropsAndRequired(schema: any): { entries: [string, any][]; required: Set<string> } {
    const root = schema;
    const seen = new Set<any>();
    const props: Record<string, any> = {};
    const req = new Set<string>();
    function walk(s: any) {
      if (!s || typeof s !== 'object' || seen.has(s)) return;
      seen.add(s);
      const n = normalizeSchema(s, root);
      if (!n || typeof n !== 'object') return;
      if (Array.isArray(n.allOf)) n.allOf.forEach(walk);
      if (n.properties && typeof n.properties === 'object') {
        for (const [k, ps] of Object.entries(n.properties)) {
          props[k] = ps;
        }
      }
      if (Array.isArray(n.required)) n.required.forEach((r: any) => typeof r === 'string' && req.add(r));
    }
    walk(schema);
    return { entries: Object.entries(props), required: req };
  }

  const initialValueForProp = (propSchema: any, root: any) => {
    const s = normalizeSchema(propSchema, root) || {};
    if (Array.isArray(s.enum) && s.enum.length) return s.enum[0];
    return getDefaultValueForType(s.type || 'string');
  };

  const getDefaultValueForType = (type: string) => {
    switch (type) {
      case 'string':
        return '';
      case 'number':
      case 'integer':
        return 0;
      case 'boolean':
        return false;
      case 'array':
        return [];
      case 'object':
        return {};
      default:
        return '';
    }
  };

  function buildInitialValueFromSchema(schema: any) {
    const root = schema;
    const norm = normalizeSchema(schema, root) || {};
    if (norm.type === 'object' || norm.properties || norm.allOf) {
      const { entries } = collectPropsAndRequired(norm);
      const obj: any = {};
      for (const [k, ps] of entries) obj[k] = initialValueForProp(ps, root);
      return obj;
    }
    return initialValueForProp(norm, root);
  }

  // Preselect schemas by heuristics: exact/contains title match to param name (case-insensitive)
  const preselectedKeys = params.map(p => {
    const lc = p.name.toLowerCase();
    const exact = SCHEMAS.find(s => s.title.toLowerCase() === lc);
    if (exact) return exact.key;
    const contains = SCHEMAS.find(s => s.title.toLowerCase().includes(lc));
    return contains?.key ?? null;
  });

  const BASIC_TYPES = ['string', 'number', 'boolean', 'object', 'array'] as const;
  type BasicType = typeof BASIC_TYPES[number];
  
  const [selectedSchemaKeys, setSelectedSchemaKeys] = useState<(string | null)[]>(preselectedKeys);
  const [selectedBasicTypes, setSelectedBasicTypes] = useState<(BasicType | '')[]>(params.map(() => ''));
  const [customTypes, setCustomTypes] = useState<string[]>(params.map(() => ''));
  const [values, setValues] = useState<any[]>(
    params.map((p, idx) => {
      const key = preselectedKeys[idx];
      const sch = getSchemaByKey(key);
      if (sch) return buildInitialValueFromSchema(sch);
      return p.type === 'object' ? {} : '';
    })
  );
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<any>(null);
  const [ran, setRan] = useState(false);

  const handleChange = (i: number, value: any) => {
    setValues(vals => vals.map((v, idx) => idx === i ? value : v));
  };

  const renderSchemaForm = (schema: any, value: any, onChange: (v: any) => void) => {
    const root = schema;
    const norm = normalizeSchema(schema, root) || {};
    if (norm.type !== 'object' && !norm.properties && !norm.allOf) {
      // Fallback for primitives at top-level
      const s = normalizeSchema(norm, root);
      if (Array.isArray(s?.enum)) {
        return (
          <Select value={String(value ?? '')} onValueChange={v => onChange(v)}>
            <SelectTrigger><SelectValue placeholder="Select value" /></SelectTrigger>
            <SelectContent>
              {s.enum.map((opt: any) => (
                <SelectItem key={String(opt)} value={String(opt)}>{String(opt)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }
      if (s?.type === 'boolean') {
        return (
          <Select value={String(value ?? '')} onValueChange={v => onChange(v === 'true')}>
            <SelectTrigger><SelectValue placeholder="Select value" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="true">True</SelectItem>
              <SelectItem value="false">False</SelectItem>
            </SelectContent>
          </Select>
        );
      }
      if (s?.type === 'number' || s?.type === 'integer') {
        return <Input value={value ?? ''} onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))} type="number" placeholder="Enter number" />;
      }
      return <Input value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder="Enter value" />;
    }

    const { entries, required } = collectPropsAndRequired(norm);
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {entries.map(([field, ps]) => {
          const s = normalizeSchema(ps, root) || {};
          const isReq = required.has(field);
          const val = value?.[field];
          const setVal = (nv: any) => onChange({ ...(value || {}), [field]: nv });
          return (
            <div key={field}>
              <label className="block text-xs mb-1">{field}{isReq ? ' *' : ''}</label>
              {Array.isArray(s.enum) ? (
                <Select value={val != null ? String(val) : ''} onValueChange={v => setVal(v)}>
                  <SelectTrigger><SelectValue placeholder="Select value" /></SelectTrigger>
                  <SelectContent>
                    {s.enum.map((opt: any) => (
                      <SelectItem key={String(opt)} value={String(opt)}>{String(opt)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : s.type === 'boolean' ? (
                <Select value={val != null ? String(val) : ''} onValueChange={v => setVal(v === 'true')}>
                  <SelectTrigger><SelectValue placeholder="Select value" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">True</SelectItem>
                    <SelectItem value="false">False</SelectItem>
                  </SelectContent>
                </Select>
              ) : s.type === 'number' || s.type === 'integer' ? (
                <Input value={val ?? ''} onChange={e => setVal(e.target.value === '' ? '' : Number(e.target.value))} type="number" placeholder={`Enter ${field}`} />
              ) : s.type === 'array' && s.items && (s.items.type === 'string' || s.items.type === 'number' || Array.isArray(s.items.enum)) ? (
                <Input
                  value={Array.isArray(val) ? val.join(',') : (val ?? '')}
                  onChange={e => {
                    const parts = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                    setVal(parts);
                  }}
                  placeholder={`Comma-separated values`}
                />
              ) : s.type === 'object' ? (
                // Nested object: fall back to JSON textarea
                <textarea
                  className="w-full font-mono text-xs border rounded p-2"
                  rows={6}
                  value={typeof val === 'string' ? val : JSON.stringify(val ?? {}, null, 2)}
                  onChange={e => {
                    try { setVal(JSON.parse(e.target.value)); } catch { setVal(e.target.value); }
                  }}
                  placeholder="Paste JSON object here"
                />
              ) : (
                <Input value={val ?? ''} onChange={e => setVal(e.target.value)} placeholder={`Enter ${field}`} />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderBasicTypeInput = (index: number, type: BasicType, value: any, onChange: (v: any) => void) => {
    switch (type) {
      case 'string':
        return (
          <Input
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder="Enter text"
          />
        );
      case 'number':
        return (
          <Input
            type="number"
            value={value}
            onChange={e => onChange(Number(e.target.value))}
            placeholder="Enter number"
          />
        );
      case 'boolean':
        return (
          <Select value={String(value)} onValueChange={v => onChange(v === 'true')}>
            <SelectTrigger><SelectValue placeholder="Select value" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="true">True</SelectItem>
              <SelectItem value="false">False</SelectItem>
            </SelectContent>
          </Select>
        );
      case 'array':
        return (
          <Input
            value={Array.isArray(value) ? value.join(', ') : ''}
            onChange={e => {
              const items = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
              onChange(items);
            }}
            placeholder="Comma-separated values"
          />
        );
      case 'object':
        return (
          <textarea
            className="w-full font-mono text-xs border rounded p-2"
            rows={6}
            value={typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
            onChange={e => {
              try {
                onChange(e.target.value ? JSON.parse(e.target.value) : {});
              } catch {
                onChange(e.target.value);
              }
            }}
            placeholder="Enter JSON object"
          />
        );
      default:
        return null;
    }
  };

  const handleRun = async () => {
    setRan(true);
    setError(null);
    setResult(null);
    try {
      const res = await fn(...values);
      setResult(res);
    } catch (e) {
      setError(e);
    }
  };

  return (
    <div>
      <div className="font-mono text-xs mb-2">{fnName}({params.map(p => p.name).join(", ")})</div>
      {params.length > 0 && (
        <div className="space-y-2 mb-4">
          {params.map((p, i) => (
            <div key={p.name}>
              <label className="block text-xs mb-1">{p.name} <span className="text-muted-foreground">[{p.type}]</span></label>
              <div className="space-y-2 mb-2">
                <div className="flex gap-2">
                  <Select
                    value={selectedSchemaKeys[i] || ''}
                    onValueChange={(v) => {
                      setSelectedSchemaKeys(keys => keys.map((k, idx) => idx === i ? v : k));
                      const sch = getSchemaByKey(v);
                      if (sch) {
                        setValues(vals => vals.map((v2, idx) => idx === i ? buildInitialValueFromSchema(sch) : v2));
                      }
                    }}
                  >
                    <SelectTrigger className="flex-1"><SelectValue placeholder="Select schema (optional)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__basic__">Basic Types</SelectItem>
                      <SelectItem value="__custom__">Custom Type</SelectItem>
                      {SCHEMAS.map(s => (
                        <SelectItem key={s.key} value={s.key}>{s.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedSchemaKeys[i] === '__basic__' && (
                    <Select
                      value={selectedBasicTypes[i] || ''}
                      onValueChange={(v) => {
                        const newBasicTypes = [...selectedBasicTypes];
                        newBasicTypes[i] = v as BasicType;
                        setSelectedBasicTypes(newBasicTypes);
                        setValues(vals => {
                          const newVals = [...vals];
                          newVals[i] = getDefaultValueForType(v);
                          return newVals;
                        });
                      }}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {BASIC_TYPES.map(type => (
                          <SelectItem key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {selectedSchemaKeys[i] === '__custom__' && (
                    <Input
                      value={customTypes[i]}
                      onChange={(e) => {
                        const newCustomTypes = [...customTypes];
                        newCustomTypes[i] = e.target.value;
                        setCustomTypes(newCustomTypes);
                      }}
                      placeholder="Enter custom type (e.g., { name: string, age: number })"
                      className="flex-1"
                    />
                  )}
                </div>
                {selectedSchemaKeys[i] === '__basic__' && selectedBasicTypes[i] && (
                  <div className="text-xs text-muted-foreground">
                    {renderBasicTypeInput(i, selectedBasicTypes[i] as BasicType, values[i], (v) => handleChange(i, v))}
                  </div>
                )}
                {selectedSchemaKeys[i] === '__custom__' && customTypes[i] && (
                  <div className="text-xs text-muted-foreground">
                    Enter a TypeScript type definition. Example: {'{ name: string, age?: number }'}
                  </div>
                )}
              </div>
              {selectedSchemaKeys[i] && selectedSchemaKeys[i] !== '__basic__' && selectedSchemaKeys[i] !== '__custom__' ? (
                renderSchemaForm(getSchemaByKey(selectedSchemaKeys[i]!)!, values[i], v => handleChange(i, v))
              ) : selectedSchemaKeys[i] === '__basic__' && selectedBasicTypes[i] ? (
                <div className="mt-2">
                  {renderBasicTypeInput(i, selectedBasicTypes[i] as BasicType, values[i], v => handleChange(i, v))}
                </div>
              ) : customTypes[i] ? (
                <textarea
                  className="w-full font-mono text-xs border rounded p-2"
                  rows={6}
                  value={typeof values[i] === 'string' ? values[i] : JSON.stringify(values[i], null, 2)}
                  onChange={e => {
                    try {
                      handleChange(i, e.target.value ? JSON.parse(e.target.value) : {});
                    } catch {
                      handleChange(i, e.target.value);
                    }
                  }}
                  placeholder={`Enter ${customTypes[i]}`}
                />
              ) : p.type === 'object' && p.fields ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {Object.entries(p.fields).map(([field, meta]) => (
                    <div key={field}>
                      <label className="block text-xs mb-1">{field}</label>
                      {meta.options ? (
                        <Select
                          value={values[i]?.[field] ?? ''}
                          onValueChange={v => handleChange(i, { ...values[i], [field]: v })}
                        >
                          <SelectTrigger><SelectValue placeholder="Select value" /></SelectTrigger>
                          <SelectContent>
                            {meta.options.map((opt: string) => (
                              <SelectItem value={opt} key={opt}>{opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : meta.type === 'array' ? (
                        <Input
                          value={Array.isArray(values[i]?.[field]) ? values[i][field].join(',') : (values[i]?.[field] ?? '')}
                          onChange={e => handleChange(i, { ...values[i], [field]: e.target.value.split(',').map((s: string) => s.trim()) })}
                          placeholder={`Comma-separated values`}
                        />
                      ) : (
                        <Input
                          value={values[i]?.[field] ?? ''}
                          onChange={e => handleChange(i, { ...values[i], [field]: e.target.value })}
                          placeholder={`Enter ${field}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              ) : p.type === 'object' ? (
                // Fallback to JSON textarea for unknown objects
                <textarea
                  className="w-full font-mono text-xs border rounded p-2"
                  rows={6}
                  value={typeof values[i] === 'string' ? values[i] : JSON.stringify(values[i], null, 2)}
                  onChange={e => {
                    try {
                      handleChange(i, JSON.parse(e.target.value));
                    } catch {
                      handleChange(i, e.target.value);
                    }
                  }}
                  placeholder="Paste JSON object here"
                />
              ) : p.type === 'boolean' ? (
                <Select value={values[i]} onValueChange={v => handleChange(i, v === 'true')}>
                  <SelectTrigger><SelectValue placeholder="Select value" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">True</SelectItem>
                    <SelectItem value="false">False</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={values[i]}
                  onChange={e => handleChange(i, e.target.value)}
                  type={p.type === 'number' ? 'number' : 'text'}
                  placeholder={`Enter ${p.name}`}
                />
              )}
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <button
          className="bg-primary text-primary-foreground rounded px-4 py-2 text-sm"
          onClick={() => {
            setRan(true);
            setError(null);
            setResult(null);
            try {
              // Convert object field values to correct types for fn
              const args = params.map((p, i) => {
                // If a JSON Schema is selected, pass the constructed value directly
                if (selectedSchemaKeys[i] && getSchemaByKey(selectedSchemaKeys[i]!)) {
                  return values[i];
                }
                if (p.type === 'object' && p.fields) {
                  // Remove empty strings and convert arrays
                  const obj: any = {};
                  Object.entries(p.fields).forEach(([field, meta]) => {
                    let val = values[i]?.[field];
                    if (meta.type === 'array' && typeof val === 'string') {
                      val = val.split(',').map((s: string) => s.trim());
                    }
                    if (val !== undefined && val !== '') obj[field] = val;
                  });
                  return obj;
                } else if (p.type === 'object') {
                  // Try parsing JSON or pass as-is
                  if (typeof values[i] === 'string') {
                    try { return JSON.parse(values[i]); } catch { return values[i]; }
                  }
                  return values[i];
                } else if (p.type === 'boolean') {
                  return values[i] === true || values[i] === 'true';
                } else if (p.type === 'number') {
                  return Number(values[i]);
                }
                return values[i];
              });
              Promise.resolve(fn(...args)).then(setResult).catch(setError);
            } catch (e) {
              setError(e);
            }
          }}
          type="button"
        >
          Run
        </button>
        <button
          className="bg-muted text-muted-foreground rounded px-4 py-2 text-sm"
          onClick={onClose}
          type="button"
        >
          Close
        </button>
      </div>
      {ran && result !== undefined && (
        <pre className="bg-muted mt-2 p-2 rounded text-xs overflow-x-auto">
          {JSON.stringify(result, null, 2)}
        </pre>
      )}
      {ran && error && (
        <pre className="bg-destructive/10 mt-2 p-2 rounded text-xs text-destructive overflow-x-auto">
          {String(error)}
        </pre>
      )}
    </div>
  );
}
